<template>
    <div class="layout-menu-container">
        <div class="menu-seller">
            <div class="menu-shop-avatar">
                <img
                    v-if="selectedShop && selectedShop.logo_url && user.level >= 3"
                    :src="
                        selectedShop.image_name
                            ? 'https://img.ep-cdn.com/images/160/48/' +
                              selectedShop.image_name.substr(0, 2) +
                              '/' +
                              selectedShop.image_name +
                              '.webp'
                            : null
                    "
                    class="mb-1 menu-shop-logo"
                    alt="Shop logo"
                />
            </div>
            <div class="menu-seller-info">
                <div v-if="selectedShop" class="menu-seller-name">{{ selectedShop.name }}</div>
                <div class="menu-seller-handle">
                    <span v-if="selectedShop">
                        @
                        <template v-if="selectedShop.country_locale_code == 'rs'">ePonuda.com</template>
                        <template v-if="selectedShop.country_locale_code == 'si'">Shoptok.si</template>
                        <template v-if="selectedShop.country_locale_code == 'hr'">Shoptok.hr</template>
                        <template v-if="selectedShop.country_locale_code == 'ba'">ePonuda.ba</template>
                    </span>
                </div>
            </div>
        </div>
        <div class="text-center mt-2 mb-2">
            <span v-if="selectedShop">
                @
                <template v-if="selectedShop.country_locale_code == 'rs'">ePonuda.com</template>
                <template v-if="selectedShop.country_locale_code == 'si'">Shoptok.si</template>
                <template v-if="selectedShop.country_locale_code == 'hr'">Shoptok.hr</template>
                <template v-if="selectedShop.country_locale_code == 'ba'">ePonuda.ba</template>
            </span>
        </div>
        <Dropdown
            v-if="shops && user.level >= 3"
            v-model="selectedShop"
            :options="getShops"
            optionLabel="name"
            optionGroupLabel="country"
            optionGroupChildren="items"
            dataKey="id"
            placeholder="Izaberite prodavnicu"
            filter
            class="menu-shop-dropdown w-full mb-4"
            @hide="hideMenu"
            ref="search"
            :disabled="shops?.length === 1"
        >
            <template #value="slotProps">
                <span class="menu-shop-value">{{
                    slotProps.value ? slotProps.value.name : slotProps.placeholder
                }}</span>
                <svg
                    class="menu-shop-chevron"
                    width="10"
                    height="10"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2.5"
                    aria-hidden="true"
                >
                    <polyline points="6 9 12 15 18 9" />
                </svg>
            </template>
            <template #optiongroup="slotProps">
                <div class="flex">
                    <div>{{ slotProps.option.country }}</div>
                </div>
            </template>
        </Dropdown>
        <!-- TODO: HIDDEN FOR THIS PHASE by design review — the score ships once
             Seller details land in BM. Re-enable by removing `v-if="false"`; the
             number below is still a placeholder and needs a real source. -->
        <div v-if="false" class="sb-score">
            <span class="sb-score-label">{{ t("components.common.appMenu.sellerScore") }}</span>
            <div class="sb-score-right">
                <div class="sb-score-val-row">
                    <span class="sb-score-num">8.4</span>
                    <span class="sb-score-den">/10</span>
                </div>
                <span class="sb-score-cta">{{ t("components.common.appMenu.sellerScoreCta") }} &rarr;</span>
            </div>
        </div>
        <AppPanelMenu :exact="false" :model="items" v-model:expandedKeys="expandedKeys" />
    </div>
</template>

<script>
import AppPanelMenu from "./AppPanelMenu";
import useHelpers from "@/composables/helpers";
import { ref, computed, watch } from "vue";
import { storeToRefs } from "pinia";
import { useI18n } from "vue-i18n";
import { useShopStore } from "../../store/shop";
import { useUserStore } from "../../store/user";
import { useRoute } from "vue-router";

export default {
    name: "AppMenu",
    components: {
        AppPanelMenu,
    },
    setup() {
        const { t } = useI18n();
        const userStore = useUserStore();
        const shopStore = useShopStore();
        const expandedKeys = ref({});
        const { user } = storeToRefs(userStore);
        const { shops, selectedShop, getShops, source } = storeToRefs(shopStore);
        const { fetchClicksDataByReport, fetchTableDataByReport, fetchData, createUserPageLog } = useHelpers();
        const search = ref(null);
        const route = useRoute();

        const items = computed(() => {
            const partner = userStore.user.level >= 3 && userStore.user.confirmed;
            const admin = userStore.user.role_id === 1;
            const page = (key, label, to, extra) => ({ key, label, to, ...extra });
            const group = (key, label, icon, pages) => ({ key, label, icon, items: pages });
            const section = (key, label, groups) => {
                const visible = groups.filter(Boolean);
                if (!visible.length) return null;
                return { key, label, section: true, items: visible };
            };

            const biddingPages = [
                page("bidding-manager", t("components.common.appMenu.biddingPreview"), "/bidding"),
                page("bidding-insights", t("components.common.appMenu.biddingAnalytics"), "/bidding/statistics"),
            ];
            if (selectedShop.value?.id === 1358) {
                biddingPages.push(page("bidding-module", t("components.common.appMenu.module"), "/module"));
            }

            const sellerPages = [
                page("store-info", t("components.common.appMenu.storeInfo"), "/profile-seller/store-info"),
                page("payment-delivery", t("components.common.appMenu.paymentDelivery"), "/profile-seller/payment-delivery"),
                page("store-locations", t("components.common.appMenu.storeLocations"), "/profile-seller/store-locations"),
            ];
            if (selectedShop.value?.sf_limit_price_c !== null && selectedShop.value?.sf_is_active_c !== "Inactive") {
                sellerPages.push(page("limit-management", t("components.common.appMenu.limitManagement"), "/limit-management"));
            }
            sellerPages.push(page("price-list", t("components.common.appMenu.priceList"), "/price-list"));

            return [
                section("section-analytics", t("components.common.appMenu.analyticsInsights"), [
                    partner &&
                        group("group-analytics", t("components.common.appMenu.analytics"), "pi pi-fw pi-chart-bar", [
                            page("click-preview", t("components.common.appMenu.clickPreview"), "/dashboard"),
                            page("click-insights", t("components.common.appMenu.analyticsOfAllClicks"), "/clicks"),
                            page("top-products", t("components.common.appMenu.topProducts"), "/products"),
                            page("top-categories", t("components.common.appMenu.topCategories"), "/category"),
                            page("devices", t("components.common.appMenu.devices"), "/devices"),
                            page("day-hour", t("components.common.appMenu.day&Hour"), "/activities"),
                            page("liquidity", t("components.common.appMenu.liquidity"), "/liquidity"),
                            page("distribution", t("components.common.appMenu.distribution"), "/distribution"),
                        ]),
                ]),
                section("section-advertising", t("components.common.appMenu.advertising"), [
                    partner &&
                        group(
                            "group-bidding",
                            t("components.common.appMenu.ePonudaRecommendedBidding"),
                            "pi pi-fw pi-credit-card",
                            biddingPages
                        ),
                    partner &&
                        group("group-product-ads", t("components.common.appMenu.productAd"), "pi pi-fw pi-hashtag", [
                            page("product-ads-smart", t("components.common.appMenu.productAdSmart"), "/product-ads-smart"),
                            page(
                                "product-ads-boost",
                                t("components.common.appMenu.productAdBoost"),
                                "/product-ads-boost",
                                { activePaths: ["/product-ads-boost"] }
                            ),
                            page("product-ads-insights", t("components.common.appMenu.productAdInsights"), "/product-ad-insights"),
                        ]),
                    partner &&
                        group("group-coupons", t("components.common.appMenu.coupons"), "pi pi-fw pi-tags", [
                            page("coupons-add", "Add coupons", "/coupons/add"),
                            page("coupons-all", "Manage coupons", "/coupons/all"),
                            page("coupons-faq", t("pages.coupons.faq.label"), "/coupons/faq"),
                        ]),
                    admin &&
                        group("group-bidding-overview", t("components.common.appMenu.biddingOverview"), "pi pi-fw pi-credit-card", [
                            page("bidding-category-overview", t("components.common.appMenu.biddingCategoryOverview"), "/bidding-category-overview"),
                            page("bidding-seller-overview", t("components.common.appMenu.biddingSellerOverview"), "/bidding-seller-overview"),
                            page("seller-end-biddings", t("components.common.appMenu.sellerEndBiddings"), "/seller-end-biddings"),
                        ]),
                    admin &&
                        group(
                            "group-product-ads-overview",
                            t("components.common.appMenu.productAdOverview"),
                            "pi pi-fw pi-credit-card",
                            [
                                page("product-ads-category-overview", t("components.common.appMenu.biddingCategoryOverview"), "/product-ads-category-overview"),
                                page("product-ads-seller-overview", t("components.common.appMenu.biddingSellerOverview"), "/product-ads-seller-overview"),
                                page("seller-end-product-ads", t("components.common.appMenu.sellerEndProductAds"), "/seller-end-product-ads"),
                            ]
                        ),
                ]),
                section("section-store", t("components.common.appMenu.storeAccount"), [
                    partner &&
                        group("group-seller-profile", t("components.common.appMenu.sellerProfile"), "pi pi-fw pi-id-card", sellerPages),
                    userStore.user.role_id !== 3 &&
                        userStore.user.invite_users &&
                        group("group-members", t("components.common.appMenu.members"), "pi pi-fw pi-users", [
                            page("my-members", t("components.common.appMenu.myMembers"), "/members"),
                            page("my-invites", t("components.common.appMenu.myInvites"), "/invites"),
                        ]),
                    partner &&
                        group("group-support", t("components.common.appMenu.customerSupport"), "pi pi-fw pi-info", [
                            page("ticket-list", t("components.common.appMenu.ticketList"), "/ticket-list"),
                            page("ticket-new", t("components.common.appMenu.newTicket"), "/ticket-new"),
                        ]),
                    group("group-growth", t("components.common.appMenu.growth"), "pi pi-fw pi-chart-line", [
                        page("growth-formula", t("components.common.appMenu.growthFormula"), "/growth-formula"),
                    ]),
                    admin &&
                        group("group-api", "Api access", "pi pi-fw pi-key", [
                            page("access-tokens", "Access tokens", "/accessTokens"),
                        ]),
                ]),
            ].filter(Boolean);
        });

        const groupKeyForPath = (sections, path) => {
            let best = null;
            for (const section of sections) {
                for (const group of section.items || []) {
                    for (const page of group.items || []) {
                        const targets = [page.to, ...(page.activePaths || [])];
                        for (const target of targets) {
                            if (!target) continue;
                            const matches = path === target || path.startsWith(`${target}/`);
                            if (matches && (!best || target.length > best.length)) {
                                best = { key: group.key, length: target.length };
                            }
                        }
                    }
                }
            }
            return best ? best.key : null;
        };

        const defaultOpenKeys = ["group-analytics", "group-bidding", "group-product-ads"];
        let defaultsApplied = false;

        const hasGroup = (sections, key) =>
            sections.some((section) => (section.items || []).some((group) => group.key === key));

        // Once, after the user is known: the first three partner groups start open,
        // plus whichever group holds the current page. Later route changes do not
        // close anything — a group stays open until the user toggles it.
        const applyDefaultOpenGroups = (sections) => {
            if (defaultsApplied) return;
            if (userStore.user == null || userStore.user.level == null) return;

            const keys = {};
            for (const key of defaultOpenKeys) {
                if (hasGroup(sections, key)) keys[key] = true;
            }
            const activeKey = groupKeyForPath(sections, route.path);
            if (activeKey) keys[activeKey] = true;
            expandedKeys.value = keys;
            defaultsApplied = true;
        };

        watch(items, applyDefaultOpenGroups, { immediate: true });

        const selectShop = async () => {
            await fetchClicksDataByReport();
            await fetchTableDataByReport();
            await fetchData();
            await createUserPageLog();
            if (route.name === "CouponsRead") {
                setTimeout(() => {
                    location.reload();
                }, 2000);
            }
        };

        const hideMenu = () => {
            search.value.filterValue = "";
            selectShop();
        };

        watch(selectedShop, () => {
            source.value =
                selectedShop.value?.country_locale_code === "rs"
                    ? t("pages.dashboard.sourceEponudaCom")
                    : selectedShop.value?.country_locale_code === "hr"
                    ? t("pages.dashboard.sourceShoptokHr")
                    : selectedShop.value?.country_locale_code === "si"
                    ? t("pages.dashboard.sourceShoptok")
                    : t("pages.dashboard.sourceShoptokBa");
        });

        return {
            // changeShop,
            expandedKeys,
            getShops,
            hideMenu,
            items,
            search,
            selectShop,
            selectedShop,
            shops,
            t,
            user,
        };
    },
};
</script>

<style lang="scss"></style>
